import React, { useState, useEffect, useContext } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Autocomplete,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import dayjs from "dayjs"
import { Gavel, Save as SaveIcon } from "@mui/icons-material"

import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import CustomBackdrop from "../../../components/CustomBackdrop"
import CustomAutocomplete from "../../../components/CustomAutocomplete"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import normalFormatDate from "../../utils/date/DDMMYYYFormatDate"

const theme = createTheme({
  palette: {
    primary: { main: "#196C87" },
    secondary: { main: "#2E7D32" },
  },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarTrasladoLegal = () => {
  const navigate = useNavigate()

  // CAPTURA DE DATOS DESDE EL STATE O URL
  const { state } = useLocation()
  const params = useParams()
  // Usamos state si viene de la grilla, o params si el usuario recarga la página
  const facnumfac = state?.factura?.facnumfac || params.id || params.facnumfac

  const { selectedMenuInfo } = useContext(GlobalContext)
  const [tabValue, setTabValue] = useState(0)

  const [formData, setFormData] = useState({
    facflaglegal: 0,
    reccodlegal: null,
    tarnumero: "NO INCAUTADA",
    motivo: "",
  })

  // 1. QUERY: Obtener Abogados
  const { data: recaudadores = [], isLoading: loadingRec } = useQuery({
    queryKey: ["getRecaudadoresLegal"],
    queryFn: async () => {
      const res = await api.get("/TrasladoaDptoLegal/getRecaudadoresLegal")
      return res.data?.data || []
    },
    refetchOnWindowFocus: false,
  })

  // 2. QUERY: Obtener Visor 360 de la Factura
  const {
    data: factura360,
    isLoading: loading360,
    refetch: refetch360,
  } = useQuery({
    queryKey: ["getFactura360Legal", facnumfac],
    queryFn: async () => {
      if (!facnumfac) return null
      const res = await api.post("/TrasladoaDptoLegal/getFactura360Legal", { facnumfac })
      if (!res.data?.success) throw new Error(res.data?.message)
      return res.data?.data
    },
    enabled: !!facnumfac,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    if (factura360?.cabecera) {
      const c = factura360.cabecera
      setFormData({
        facflaglegal: c.facflaglegal || 0,
        reccodlegal: c.reccodlegal ? { value: c.reccodlegal } : null,
        tarnumero: c.tarnumero === "INCAUTADA" ? "INCAUTADA" : "NO INCAUTADA",
        motivo: "",
      })
    }
  }, [factura360])

  // 3. MUTATION BLINDADA
  const { mutateAsync: guardarTransaccion, isPending: isSaving } = useMutation({
    queryKey: ["isSavingTrasladoLegal"],
    fn: async (payload) => {
      return await api.post("/TrasladoaDptoLegal/trasladarDptoLegal", payload)
    },
    showError: "toast",
    showSuccess: "toast",
  })

  const handleSubmit = async (e) => {
    if (e) e.preventDefault()
    if (!facnumfac) return showWarning("No se ha cargado una factura válida.")
    if (!formData.reccodlegal) return showWarning("Debe seleccionar un Abogado/Recaudador.")
    if (!formData.motivo || formData.motivo.trim().length < 5)
      return showWarning("Debe ingresar un motivo válido para la bitácora.")

    if (factura360?.cabecera?.facstatus === "A") return showWarning("No puede trasladar una factura Anulada.")

    try {
      const res = await guardarTransaccion({
        facnumfac,
        clicodigo: factura360.cabecera.clicodigo,
        facflaglegal: formData.facflaglegal,
        reccodlegal: formData.reccodlegal.value || formData.reccodlegal.reccodigo,
        tarnumero: formData.tarnumero,
        motivo: formData.motivo,
      })

      const data = res?.data || res

      if (data && data.success) {
        // Swal.fire("Éxito", data.message || "Guardado exitosamente", "success")
        refetch360()
        setFormData((prev) => ({ ...prev, motivo: "" }))
        setTabValue(2)
      } else {
        // Swal.fire("Error", data?.message || "Error al guardar", "error")
      }
    } catch (error) {
      console.error("Error al trasladar a legal:", error)
      // Swal.fire("Error", "Error al procesar la solicitud", "error")
    }
  }

  const ejecutarAction = selectedMenuInfo?.data?.barraAcciones?.find(
    (action) => action?.acccaption === "GRABAR" || action?.acccaption === "EJECUTAR",
  )
  const toolbarActions = ejecutarAction
    ? [
        {
          label: ejecutarAction.acccaption,
          key: ejecutarAction.acccaption,
          icon: getIconComponent(ejecutarAction.accnameicono, ejecutarAction.acctipoico),
        },
      ]
    : [{ label: "GRABAR", key: "GRABAR", icon: <SaveIcon /> }]

  const cabecera = factura360?.cabecera || {}
  const cartera = factura360?.cartera || []
  const bitacora = factura360?.bitacora || []

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSaving || loading360}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1, bgcolor: "white" }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
            color: "#196C87",
          }}
        >
          <Gavel sx={{ fontSize: 32, mr: 1 }} />
          <b>Detalle de Deuda Legal: {facnumfac || ""}</b>
        </div>

        <CustomBackdrop isLoading={loading360 || loadingRec || isSaving || !facnumfac} />

        {facnumfac ? (
          <Box sx={StyledRoot}>
            <Paper
              elevation={1}
              sx={{ p: 2, mb: 3, borderRadius: 2, borderLeft: "6px solid #196C87", bgcolor: "white" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="textSecondary">
                    Cliente
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {cabecera.clinombre} ({cabecera.clicodigo})
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="caption" color="textSecondary">
                    Emisión / Vencimiento
                  </Typography>
                  <Typography variant="body2">
                    {cabecera.facfecemi ? normalFormatDate(cabecera.facfecemi) : ""} -{" "}
                    {cabecera.facfecven ? normalFormatDate(cabecera.facfecven) : ""}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="caption" color="textSecondary">
                    Total Factura
                  </Typography>
                  <Typography variant="body1">${Number(cabecera.factotal || 0).toFixed(2)}</Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="caption" color="textSecondary">
                    Abono
                  </Typography>
                  <Typography variant="body1" color="success.main">
                    ${Number(cabecera.facabono || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Typography variant="caption" color="error.main" fontWeight="bold">
                    Saldo Deuda
                  </Typography>
                  <Typography variant="h6" color="error.main" fontWeight="bold">
                    ${Number(cabecera.facsaldo || 0).toFixed(2)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(e, val) => setTabValue(val)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label="Acción Legal" />
                <Tab label={`Cartera (${cartera.length})`} />
                <Tab label={`Bitácora (${bitacora.length})`} />
              </Tabs>
            </Box>

            <Paper
              component="form"
              onSubmit={handleSubmit}
              elevation={1}
              sx={{ p: 3, borderRadius: 2, bgcolor: "white", minHeight: "35vh" }}
            >
              {tabValue === 0 && (
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary" gutterBottom>
                    Actualización de Estado Legal
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Instancia Legal"
                        value={formData.facflaglegal}
                        onChange={(e) => setFormData({ ...formData, facflaglegal: e.target.value })}
                        size="small"
                      >
                        <MenuItem value={0}>Devolver a Cobranza (No Legal)</MenuItem>
                        <MenuItem value={1}>Notificación PreLegal</MenuItem>
                        <MenuItem value={2}>Demanda / En Legal</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          width: "100%",
                          "& .MuiAutocomplete-root": { width: "100%" },
                          "& .MuiFormControl-root": { width: "100%" },
                        }}
                      >
                        <Autocomplete
                          size="small"
                          options={recaudadores}
                          // Ajusta "recdescri" según la propiedad que contenga el nombre del abogado/recaudador
                          getOptionLabel={(option) => (option ? `${option.recnombre} ${option.reccodigo}` : "")}
                          value={recaudadores.find((r) => r.reccodigo === formData.reccodlegal?.value) || null}
                          onChange={(event, newValue) => {
                            setFormData({
                              ...formData,
                              reccodlegal: newValue ? { value: newValue.reccodigo } : null,
                            })
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Recaudador / Abogado *"
                              placeholder="Seleccione el profesional..."
                              // Esta propiedad es la clave: fuerza a la etiqueta a incrustarse en el borde
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              height: "40px",
                              paddingTop: "2px", // Pequeño ajuste para centrar el texto verticalmente al forzar 40px
                              paddingBottom: "2px",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        label="Estado Vehículo (Moto)"
                        value={formData.tarnumero}
                        onChange={(e) => setFormData({ ...formData, tarnumero: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="NO INCAUTADA">No Incautada</MenuItem>
                        <MenuItem value="INCAUTADA">Incautada</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Observación para la Bitácora *"
                        multiline
                        rows={4}
                        value={formData.motivo}
                        onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                        helperText="Este comentario es requerido y quedará grabado inmutablemente."
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {tabValue === 1 && (
                <TableContainer component={Paper} elevation={0} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "#f8f9fa" }}>
                        <TableCell>
                          <b>Secuencia</b>
                        </TableCell>
                        <TableCell>
                          <b>Vencimiento</b>
                        </TableCell>
                        <TableCell>
                          <b>Estado</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Total Cuota</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Abono</b>
                        </TableCell>
                        <TableCell align="right">
                          <b>Saldo</b>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {cartera.map((c) => (
                        <TableRow key={c.facsecuen} hover>
                          <TableCell>{c.facsecuen}</TableCell>
                          <TableCell>{normalFormatDate(c.facfecven)}</TableCell>
                          <TableCell>
                            {c.facstatus === "P" ? (
                              <Chip label="Pendiente" size="small" color="warning" />
                            ) : (
                              <Chip label="Aplicado" size="small" color="success" />
                            )}
                          </TableCell>
                          <TableCell align="right">${Number(c.factotal).toFixed(2)}</TableCell>
                          <TableCell align="right">${Number(c.facabono).toFixed(2)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "bold", color: c.facsaldo > 0 ? "error.main" : "inherit" }}
                          >
                            ${Number(c.facsaldo).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {tabValue === 2 && (
                <Box>
                  {bitacora.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mt: 2, fontStyle: "italic", textAlign: "center" }}
                    >
                      Aún no existen registros en la bitácora legal.
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: "40vh", overflow: "auto", pr: 2 }}>
                      {bitacora.map((b, idx) => (
                        <Paper
                          key={idx}
                          elevation={0}
                          variant="outlined"
                          sx={{ p: 2, mb: 2, borderLeft: "4px solid #196C87" }}
                        >
                          <Box display="flex" justifyContent="space-between" mb={1}>
                            <Typography variant="caption" fontWeight="bold" color="primary">
                              {dayjs(b.obsfecisys).format("DD/MM/YYYY HH:mm")} - Acción: {b.obsaccion}
                            </Typography>
                            <Chip label={`Usuario: ${b.obsusuisys}`} size="small" variant="outlined" />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ bgcolor: "#f5f5f5", p: 1.5, borderRadius: 1, fontStyle: "italic" }}
                          >
                            "{b.obsobserva}"
                          </Typography>
                          <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                            <Typography variant="caption" color="textSecondary">
                              <b>Saldo a la fecha:</b> ${Number(b.obsvalche).toFixed(2)}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              <b>Vehículo:</b> {b.tranumbco}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              <b>Asignado a:</b> {b.obsnumche}
                            </Typography>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        ) : (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography color="error">
              No se recibieron datos de la factura. Regrese a la bandeja e intente nuevamente.
            </Typography>
          </Paper>
        )}
      </div>
    </ThemeProvider>
  )
}

export default EditarTrasladoLegal
